/**
 * Utilitário para detectar a plataforma onde o app está rodando
 * Usado para diferenciar comportamento entre Web, PWA e App Nativo (Capacitor)
 */

import { Capacitor } from '@capacitor/core';

/**
 * Verifica se o app está rodando como app nativo (Android/iOS via Capacitor)
 * @returns {boolean}
 */
export const isNativeApp = () => Capacitor.isNativePlatform();

/**
 * Verifica se está rodando no Android nativo
 * @returns {boolean}
 */
export const isAndroid = () => Capacitor.getPlatform() === 'android';

/**
 * Verifica se está rodando no iOS nativo
 * @returns {boolean}
 */
export const isIOS = () => Capacitor.getPlatform() === 'ios';

/**
 * Verifica se está rodando no navegador web (inclui PWA)
 * @returns {boolean}
 */
export const isWeb = () => Capacitor.getPlatform() === 'web';

/**
 * Verifica se o app está rodando como PWA instalado (Standalone)
 * @returns {boolean}
 */
export const isPWA = () => {
    return window.matchMedia('(display-mode: standalone)').matches || 
           (window.navigator.standalone) || 
           document.referrer.includes('android-app://') ||
           window.location.search.includes('mode=standalone');
};

/**
 * Retorna a plataforma atual
 * @returns {'android' | 'ios' | 'web' | 'ios-web' | 'android-web' | 'pwa'}
 */
export const getPlatform = () => {
    // Se estiver em modo standalone, reportamos como PWA indepentende do SO
    if (isPWA()) return 'pwa';

    const platform = Capacitor.getPlatform();
    
    // Se estiver na web, tenta detectar se é um dispositivo móvel via UserAgent
    if (platform === 'web') {
        const ua = navigator.userAgent.toLowerCase();
        
        // Detecção robusta de iOS (iPhone, iPad, iPod)
        // O check de 'macintosh' com touchPoints > 1 serve para iPads em modo desktop
        const isIOSDevice = /iphone|ipad|ipod/.test(ua) || 
                           (ua.includes('macintosh') && navigator.maxTouchPoints > 1);
        
        if (isIOSDevice) return 'ios-web';
        
        // Detecção de Android
        if (/android/.test(ua)) return 'android-web';
    }
    
    return platform;
};

let _cachedIsDirect = null;

/**
 * Detecta se a distribuição atual é direta (APK avulso / Sideload com pagamentos Web)
 * Verifica a variável de build REACT_APP_DISTRIBUTION_CHANNEL ou o package ID do app nativo.
 * @returns {Promise<boolean>}
 */
export const isDirectDistribution = async () => {
    if (_cachedIsDirect !== null) return _cachedIsDirect;

    // Se estiver explicitamente definido no .env ou tempo de build
    if (process.env.REACT_APP_DISTRIBUTION_CHANNEL === 'direct') {
        _cachedIsDirect = true;
        return true;
    }

    // Se for Web / PWA pura, as compras são sempre Web (Mercado Pago)
    if (!isNativeApp()) {
        _cachedIsDirect = true;
        return true;
    }

    // Se for nativo, verifica pelo ID do pacote via Capacitor App
    try {
        const { App: CapacitorApp } = await import('@capacitor/app');
        const info = await CapacitorApp.getInfo();
        const isDirectId = info?.id?.includes('direct') || info?.id?.endsWith('.apk');
        _cachedIsDirect = !!isDirectId;
        return _cachedIsDirect;
    } catch (e) {
        console.warn('Erro ao verificar package id em isDirectDistribution:', e);
        _cachedIsDirect = false;
        return false;
    }
};

/**
 * Versão síncrona para checagem rápida em renderização baseada em env ou cache
 * @returns {boolean}
 */
export const isDirectDistributionSync = () => {
    if (process.env.REACT_APP_DISTRIBUTION_CHANNEL === 'direct') return true;
    if (!isNativeApp()) return true;
    return _cachedIsDirect === true;
};

/**
 * Retorna o canal de distribuição atual do aplicativo.
 * Usado pelo painel admin para identificar quem está no APK direto.
 * @returns {Promise<'direct' | 'playstore' | 'appstore' | 'web' | 'pwa' | 'unknown'>}
 */
export const getDistributionChannel = async () => {
    if (!isNativeApp()) return isPWA() ? 'pwa' : 'web';

    const platform = Capacitor.getPlatform();
    const isDirect = await isDirectDistribution();
    if (isDirect) return 'direct';
    if (platform === 'android') return 'playstore';
    if (platform === 'ios') return 'appstore';
    return 'unknown';
};

/**
 * Informações de distribuição enviadas ao Firestore/admin.
 * Inclui o appId nativo (ex: com.meupainel.app.direct) para auditoria.
 * @returns {Promise<{ distributionChannel: string, appId: string | null }>}
 */
let _cachedAppId;
export const getAppDistributionInfo = async () => {
    const distributionChannel = await getDistributionChannel();

    if (isNativeApp() && _cachedAppId === undefined) {
        try {
            const { App: CapacitorApp } = await import('@capacitor/app');
            const info = await CapacitorApp.getInfo();
            _cachedAppId = info?.id || null;
        } catch (e) {
            console.warn('Erro ao obter appId em getAppDistributionInfo:', e);
            _cachedAppId = null;
        }
    }

    return { distributionChannel, appId: _cachedAppId ?? null };
};

